import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import {
  ScreeningBenchmarkData,
  ScreeningPendingReview,
} from '@scale/shared/taskerScreening/types';
import API from '@scale/frontend-lib/dashboard-api';
import { CircularProgress } from 'react-scale/components/design-system';
import { Button } from '@scale/scaleui';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import Table from 'corp/components/UI/Table';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { ScreeningReviewQualificationSectionStatus } from '@scale/shared/types/qualification/types';
import TaskerScreeningGradingReviewModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningGradingReviewModal';
import TaskerScreeningBenchmarkAssignPermissionsModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningBenchmarkAssignPermissionsModal';

interface TaskerScreeningBenchmarkReviewToolProps {
  user: IUserJson;
  needsFetch: boolean;
  setNeedsFetch: (needsFetch: boolean) => void;
}

const tableHeaders: Record<string, string> = {
  id: 'Benchmark ID',
  preview: 'Preview',
  enabled: 'Status',
  type: 'Type',
  toggleEnabled: 'Update',
  assignBenchmark: 'Assign',
  createdBy: 'Created By',
  createdByEmail: 'Created By Email',
  createdAt: 'Created At',
  workerSkillName: 'Skill',
  workerSkillId: 'Skill ID',
  course: 'Course',
  sectionTitle: 'Course Section Title',
  expectedScore: 'Expected Score',
  averageActualScore: 'Average Score',
  averageAccuracy: 'Average Accuracy',
  numAttempts: 'Num Attempts',
};

const shownTableHeaders = [
  'id',
  'enabled',
  'type',
  'toggleEnabled',
  'assignBenchmark',
  'preview',
  'workerSkillName',
  'expectedScore',
  'averageActualScore',
  'averageAccuracy',
  'numAttempts',
];

const filterableTableHeaders = [
  'id',
  'createdBy',
  'createdAt',
  'type',
  'enabled',
  'workerSkillName',
  'expectedScore',
  'averageActualScore',
  'averageAccuracy',
  'numAttempts',
];

const smallColumns = ['preview', 'enabled', 'toggleEnabled', 'createdAt', 'type'];

const TaskerScreeningBenchmarkReviewTool: React.FC<TaskerScreeningBenchmarkReviewToolProps> = ({
  needsFetch,
  setNeedsFetch,
}) => {
  const [benchmarks, setBenchmarks] = useState<ScreeningBenchmarkData[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [roleBenchmarkId, setRoleBenchmarkId] = useState<string>();
  const [previewBenchmark, setPreviewBenchmark] = useState<ScreeningPendingReview>();

  const handleFetchBenchmarks = useCallback(async () => {
    setLoadingTable(true);
    const { benchmarks } = await API.genai.fetchScreeningBenchmarkData({});
    setBenchmarks(benchmarks);
    setNeedsFetch(false);
    setLoadingTable(false);
  }, [setNeedsFetch]);

  const updateBenchmarkEnabled = async (id: string, enabled: boolean) => {
    await API.genai.updateScreeningBenchmarkEnabled({ id, enabled });
  };

  const handleUpdateBenchmarkEnabled = useCallback(
    async (id: string, enabled: boolean) => {
      setLoadingUpdate(true);
      await updateBenchmarkEnabled(id, enabled);
      setBenchmarks(
        benchmarks.map(benchmark => {
          if (benchmark.id === id) {
            return {
              ...benchmark,
              enabled,
            };
          }
          return benchmark;
        }),
      );
      setLoadingUpdate(false);
    },
    [benchmarks],
  );

  useEffect(() => {
    if (needsFetch) {
      handleFetchBenchmarks();
    }
  }, [handleFetchBenchmarks, needsFetch]);

  const tableData = useMemo(() => {
    const data = benchmarks.map(benchmark => ({
      id: benchmark.id,
      enabled: benchmark.enabled ? 'Enabled' : 'Disabled',
      toggleEnabled: (
        <Button
          onClick={() => {
            handleUpdateBenchmarkEnabled(benchmark.id, !benchmark.enabled);
          }}
          variant={benchmark.enabled ? 'danger' : 'primary'}
          disabled={loadingUpdate}
        >
          {benchmark.enabled ? 'Disable' : 'Enable'}
        </Button>
      ),
      preview: (
        <Button
          onClick={() => {
            setPreviewBenchmark({
              _id: '',
              workerSkillId: benchmark.workerSkillId,
              workerSkillName: benchmark.workerSkillName,
              workerSkillDimension: benchmark.workerSkillDimension,
              worker: '',
              workerEmail: '',
              course: benchmark.course,
              status: ScreeningReviewQualificationSectionStatus.Pending,
              sectionTitle: benchmark.sectionTitle,
              questions: benchmark.questions,
              answers: benchmark.answers,
              isRewrite: benchmark.isRewrite,
              rewrittenOriginalAnswers: benchmark.rewrittenOriginalAnswers,
              description: benchmark.description,
              createdAt: benchmark.createdAt,
            });
            setOpenPreviewModal(true);
          }}
          variant="secondary"
        >
          Preview
        </Button>
      ),
      assignBenchmark: (
        <Button
          onClick={() => {
            setRoleBenchmarkId(benchmark.id);
            setOpenRoleModal(true);
          }}
          disabled={!benchmark.enabled || loadingUpdate}
          variant="secondary"
        >
          Assign to Permissions
        </Button>
      ),
      createdBy: <SmartLink type={INamedObjectTypes.User} id={benchmark.createdBy} />,
      createdByEmail: benchmark.createdByEmail,
      createdAt: benchmark.createdAt.toString().split('T')[0],
      workerSkillId: benchmark.workerSkillId,
      workerSkillName: benchmark.workerSkillName,
      course: benchmark.course,
      type: benchmark.isRewrite ? 'Text Rewrite' : 'Text',
      sectionTitle: benchmark.sectionTitle,
      expectedScore: benchmark.expectedScore,
      averageActualScore: benchmark.averageActualScore,
      averageAccuracy: benchmark.averageAccuracy,
      numAttempts: benchmark.numAttempts,
    }));
    return data;
  }, [benchmarks, handleUpdateBenchmarkEnabled, loadingUpdate]);

  const columns = useMemo(() => {
    return Object.keys(tableHeaders).map(label => ({
      Header: tableHeaders[label],
      accessor: label,
      show: shownTableHeaders.includes(label),
      filterable: filterableTableHeaders.includes(label),
      ...(smallColumns.includes(label) && { minWidth: 60 }),
    }));
  }, []);

  return (
    <div>
      {loadingTable ? (
        <CircularProgress />
      ) : (
        <div>
          <Table
            filterable
            data={tableData}
            columns={columns}
            defaultFilterMethod={(filter, row) => {
              const rowValue =
                filter.id === 'createdBy'
                  ? `${row[filter.id].props.id}${row.createdByEmail}`
                  : filter.id === 'workerSkillName'
                  ? `${row[filter.id]}${row.workerSkillName}`
                  : row[filter.id];
              return (rowValue ?? '').toLowerCase().includes(filter.value.toLowerCase());
            }}
          />
          {previewBenchmark && (
            <TaskerScreeningGradingReviewModal
              review={previewBenchmark}
              open={openPreviewModal}
              setOpen={setOpenPreviewModal}
              updateReviewScore={() => {}}
            />
          )}
          {roleBenchmarkId && (
            <TaskerScreeningBenchmarkAssignPermissionsModal
              benchmarkId={roleBenchmarkId}
              open={openRoleModal}
              onClose={() => setOpenRoleModal(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TaskerScreeningBenchmarkReviewTool;
